import { useEffect, useRef } from "react";
import Quill from "quill";

interface Props {
  /** Initial HTML content. Subsequent updates are NOT pushed into the editor —
   * remount via the `key` prop when the supplier ID changes. */
  initialHtml: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  ariaLabelledBy?: string;
  placeholder?: string;
}

/**
 * Thin React wrapper around Quill 2.x.
 *
 * The Quill instance owns the editing state while mounted; `onChange` mirrors
 * the current HTML out to react-hook-form / the store. To avoid the
 * controlled-component infinite loop (external value change → set Quill HTML
 * → text-change → onChange → external value change → ...), we ONLY seed
 * Quill from `initialHtml` on mount. Use a `key` prop derived from the
 * supplier ID to force a remount when a fresh prefill lands.
 */
export function RichTextEditor({
  initialHtml,
  onChange,
  onBlur,
  ariaLabelledBy,
  placeholder,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);

  // Latest onChange/onBlur in a ref so we don't have to put them in the
  // useEffect dep array (which would force re-init on every parent render).
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  onChangeRef.current = onChange;
  onBlurRef.current = onBlur;

  useEffect(() => {
    if (!containerRef.current || quillRef.current) return;

    const quill = new Quill(containerRef.current, {
      theme: "snow",
      placeholder,
      modules: {
        toolbar: [
          [{ header: [false, 1, 2, 3] }],
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link"],
          ["clean"],
        ],
      },
    });

    if (initialHtml) {
      quill.clipboard.dangerouslyPasteHTML(initialHtml);
    }

    quill.on("text-change", () => {
      onChangeRef.current(quill.root.innerHTML);
    });

    quill.on("selection-change", (range) => {
      // null range means editor lost focus
      if (range === null && onBlurRef.current) {
        onBlurRef.current();
      }
    });

    quillRef.current = quill;
    // Mount-only effect; we intentionally don't depend on initialHtml/onChange/onBlur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={containerRef} aria-labelledby={ariaLabelledBy} />;
}
